import type { TtqMembershipPressureRecommendation } from '../services/ttqMembershipPressureService';

export type TtqMembershipPressurePeerMembership = NonNullable<
  TtqMembershipPressureRecommendation['suggestedPeerMemberships']
>[number];

export function recommendationListTitle(rec: TtqMembershipPressureRecommendation): string {
  return String(rec.sourceMainQuestion || rec.sourceParticleTitle || '').trim()
    || rec.sourceParticleId.slice(0, 8);
}

export function recommendationListMeta(rec: TtqMembershipPressureRecommendation): string {
  if (rec.action === 'join_existing_ttqa') {
    const target = String(rec.targetTtqaTitle || '').trim()
      || (rec.targetTtqaId ? `品题 ${rec.targetTtqaId.slice(0, 8)}` : '已有品题');
    return `建议并入「${target}」 · Ring${rec.ringEvidence?.ring ?? '—'} · 分 ${(rec.scores?.total ?? 0).toFixed(2)}`;
  }
  return `建议新开品题 · 同伴 ${rec.suggestedPeerIds.length} · Ring${rec.ringEvidence?.ring ?? '—'} · 分 ${(rec.scores?.total ?? 0).toFixed(2)}`;
}

export function recommendationStemText(
  main: string | null | undefined,
  title: string | null | undefined,
  fallbackId: string,
): string {
  return String(main || title || '').trim() || fallbackId.slice(0, 8);
}

export function recommendationFusionParticleCount(rec: TtqMembershipPressureRecommendation): number {
  const peers = (rec.suggestedPeerIds || [])
    .map((id) => String(id || '').trim())
    .filter((id) => id && id !== rec.sourceParticleId);
  return 1 + new Set(peers).size;
}

export function recommendationFusionOutcomeLabel(rec: TtqMembershipPressureRecommendation): string {
  const count = recommendationFusionParticleCount(rec);
  if (rec.action === 'join_existing_ttqa') {
    const target = String(rec.targetTtqaTitle || '').trim()
      || (rec.targetTtqaId ? `品题 ${rec.targetTtqaId.slice(0, 8)}` : '该品题');
    return count <= 1
      ? `扶正后将把 1 道知识颗粒并入「${target}」`
      : `扶正后将把 ${count} 道知识颗粒并入「${target}」`;
  }
  return `扶正后将把 ${count} 道知识颗粒融合成 1 个品题`;
}

export function recommendationApproveButtonCopy(
  rec: TtqMembershipPressureRecommendation,
  approving: boolean,
): { title: string; subtitle: string } {
  const count = recommendationFusionParticleCount(rec);
  if (rec.action === 'join_existing_ttqa') {
    return {
      title: approving ? '并入中…' : '确认并入该品题',
      subtitle: count <= 1
        ? '将 1 道知识颗粒并入该品题'
        : `将 ${count} 道知识颗粒并入该品题`,
    };
  }
  return {
    title: approving ? '处理中…' : '构造品题（扶正入围）',
    subtitle: `将 ${count} 道知识颗粒融合成 1 个品题`,
  };
}

function sessionTitle(session: { id: string; title: string | null }): string {
  return String(session.title || '').trim() || `品题 ${session.id.slice(0, 8)}`;
}

export function formatParticleMembershipLabel(
  membership: TtqMembershipPressurePeerMembership | null | undefined,
): string {
  const sessions = membership?.sessions || [];
  if (!membership?.inTtqa || sessions.length === 0) {
    return '尚未加入品题';
  }
  const names = sessions.slice(0, 2).map(sessionTitle);
  if (sessions.length === 1) {
    return `已加入「${names[0]}」`;
  }
  if (sessions.length === 2) {
    return `已加入「${names[0]}」「${names[1]}」`;
  }
  return `已加入「${names[0]}」等 ${sessions.length} 个品题`;
}

export function formatSpineLabel(label: string | null | undefined): string {
  return String(label || '').trim() || '学科主干未绑定';
}

export function formatPressureAbilityKey(key: string): string {
  const raw = String(key || '').trim();
  if (!raw) return '';
  if (raw.toLowerCase().startsWith('label:')) return raw.slice(6).trim();
  if (raw.toLowerCase().startsWith('id:')) {
    const id = raw.slice(3).trim();
    return id.length > 8 ? `能力 ${id.slice(0, 8)}` : `能力 ${id}`;
  }
  return raw;
}

export function formatPressureLabelList(
  labels: string[] | null | undefined,
  empty = '无',
): string {
  const items = (labels || []).map(formatPressureAbilityKey).filter(Boolean);
  return items.length > 0 ? items.join(' · ') : empty;
}

export function formatPressureScoreLine(scores: {
  trunkProximity?: number;
  typeHomogeneity?: number;
  implicitAbility?: number;
  mathStructure?: number;
  total?: number;
} | null | undefined): string {
  const n = (value: number | undefined) => (
    Number.isFinite(value) ? Number(value).toFixed(2) : '—'
  );
  return [
    `主干 ${n(scores?.trunkProximity)}`,
    `同质 ${n(scores?.typeHomogeneity)}`,
    `能力 ${n(scores?.implicitAbility)}`,
    `结构 ${n(scores?.mathStructure)}`,
    `综合 ${n(scores?.total)}`,
  ].join(' · ');
}

export function formatPressureWeightLine(weights: {
  trunkProximity?: number;
  typeHomogeneity?: number;
  implicitAbility?: number;
  mathStructure?: number;
} | null | undefined): string {
  const pct = (value: number | undefined) => (
    Number.isFinite(value) ? `${Math.round(Number(value) * 100)}%` : '—'
  );
  return [
    `主干 ${pct(weights?.trunkProximity ?? 0.15)}`,
    `同质 ${pct(weights?.typeHomogeneity ?? 0.40)}`,
    `能力 ${pct(weights?.implicitAbility ?? 0.40)}`,
    `结构 ${pct(weights?.mathStructure ?? 0.05)}`,
  ].join(' · ');
}

export function peerScoreSeriesFor(
  rec: TtqMembershipPressureRecommendation | null | undefined,
  particleId: string,
) {
  return (rec?.scoreSeries || []).find((item) => item.particleId === particleId) || null;
}

export function recommendationPeerMembershipSummary(rec: TtqMembershipPressureRecommendation): string | null {
  const total = (rec.suggestedPeerIds || []).length;
  if (total === 0) return null;
  const memberships = rec.suggestedPeerMemberships || [];
  const alreadyIn = rec.suggestedPeerIds.filter((_, index) => memberships[index]?.inTtqa).length;
  const notIn = total - alreadyIn;
  if (alreadyIn === 0) {
    return `${total} 个同伴均尚未加入品题`;
  }
  if (notIn === 0) {
    return `${total} 个同伴均已加入其他品题`;
  }
  return `${total} 个同伴：${notIn} 个尚未加入品题，${alreadyIn} 个已在其他品题中`;
}

export function recommendationNearestRingLabel(ring: 0 | 1 | 2 | undefined | null): string {
  if (ring === 0) return '最近环 Ring0（同主干节点）';
  if (ring === 1) return '最近环 Ring1（同主干、不同节点）';
  if (ring === 2) return '最近环 Ring2（跨主干、靠隐性能力）';
  return '最近环 —';
}

export const MEMBERSHIP_PRESSURE_SCORING_HELP = [
  '主干只用来找候选（Ring0 同节点、Ring1 同主干、Ring2 跨主干靠能力），不能单独决定是否融合成品题。',
  '同质 = 两道题 PAP 考点流标签的 Jaccard（共享考查点 / 并集）。任一侧没有考查点则为 0。',
  '能力 = 两道题 PAP 隐性能力标签的 Jaccard；「转化化归 / 逻辑推理」等万能标签会降权。',
  '综合分 = 同质×40% + 能力×40% + 主干×15% + 结构×5%（结构暂为 0）。',
  '过闸：每道同伴都要 同质 ≥ 0.12 且 能力 ≥ 0.15 且 综合 ≥ 0.22。新开品题还要同伴平均 同质/能力 ≥ 0.15、综合 ≥ 0.32。',
  '并入已有品题更严（同质平均 ≥ 0.15、能力平均 ≥ 0.15、综合 ≥ 0.38）。',
] as const;

export function buildRecommendationDebugPayload(rec: TtqMembershipPressureRecommendation): string {
  return [
    `recommendationId: ${rec.id}`,
    `action: ${rec.action}`,
    `status: ${rec.status}`,
    `sourceParticleId: ${rec.sourceParticleId}`,
    `targetTtqaId: ${rec.targetTtqaId || ''}`,
    `suggestedPeerIds: ${(rec.suggestedPeerIds || []).join(', ')}`,
    `ring: ${rec.ringEvidence?.ring ?? ''}`,
    `scores.total: ${rec.scores?.total ?? ''}`,
    `scores.trunk: ${rec.scores?.trunkProximity ?? ''}`,
    `scores.type: ${rec.scores?.typeHomogeneity ?? ''}`,
    `scores.ability: ${rec.scores?.implicitAbility ?? ''}`,
  ].join('\n');
}
